import { useState } from 'react';
import useWaitingMutation from '@common/hooks/useWaitingMutation';
import { AxiosProgressEvent } from 'axios';
import { uploadAxiosFiles } from '../../services/requestService';
import { ValidationError } from '@common/types';
import { Payload } from './types';
import { IMappedAxiosError } from '@common/api/uploadFileWithProgression';

export const useUploadFile = (onError: () => void) => {
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [fileName, setFileName] = useState('');
  const [fileErrors, setFileErrors] = useState<ValidationError[]>([]);
  const [isFileProcessing, setIsFileProcessing] = useState(false);

  const handleUploadProgress = (p: AxiosProgressEvent) => setUploadProgress(Math.floor((p.loaded / p.total!) * 100));

  const handleSuccess = (fileName: string) => {
    setFileName(fileName);
    setIsFileProcessing(false);
  };

  const clearFileName = () => setFileName('');

  const getErrors = (e: IMappedAxiosError) => {
    const clonedErrors = [];
    const isVirusCheckServiceError = e.cause === 503 && e.data?.errorMessage === 'Virus scan service is unavailable';
    const isVirusCheckError = e.cause === 400 && e.data?.errorMessage === 'Virus detected in the attached file';
    const isCorruptedFileError = e.cause === 400 && e.data?.errorMessage === 'Corrupted file';
    const isEmptyFileError = e.cause === 400 && e.data?.errorMessage === 'Attached file appear to be empty';

    if (isCorruptedFileError) {
      const corruptedFileMessage = {
        message: 'Corrupted file',
        subMessage: 'We could not open the file. The file is probably an old word document, only new format is accepted.',
      };
      onError();
      return [corruptedFileMessage];
    }

    if (isVirusCheckError) {
      const virusErrorMessage = {
        message: 'Virus detected',
        subMessage: 'Your file could not be uploaded because it contains a virus',
      };
      onError();
      return [virusErrorMessage];
    }

    if (isVirusCheckServiceError) {
      const virusErrorMessage = {
        message: 'Virus scan service unavailable',
        subMessage: 'Your file could not be uploaded because the virus scan service is currently unavailable. Please try again later.',
      };
      onError();
      return [virusErrorMessage];
    }
    
    if (isEmptyFileError) {
      const emptyFileMessage = {
        message: 'Empty file',
        subMessage: 'Your file could not be uploaded because it appears to be empty, please try reuploading it',
      };
      onError();
      return [emptyFileMessage];
    }

    clonedErrors.push({
      message: 'File validation error',
      subMessage: 'File validation did not succeed',
    });

    onError();

    return clonedErrors;
  };

  const handleError = (e: IMappedAxiosError) => {
    const errors = getErrors(e);
    setFileErrors(errors);
    setIsFileProcessing(false);
  };

  const { mutate, isLoading } = useWaitingMutation(
    (payload: Payload) => {
      setIsFileProcessing(true);
      setUploadProgress(0);
      const formData = new FormData();
      formData.append('file', payload.file);
      formData.append('documentType', payload.documentType);
      formData.append('fileRole', payload.fileRole ?? '');

      return uploadAxiosFiles(formData, handleUploadProgress);
    },
    {
      onSuccess: handleSuccess,
      onError: handleError,
    },
  );

  return { uploadFile: mutate, isLoading, uploadProgress, fileName, fileErrors, isFileProcessing, setFileErrors, clearFileName };
};
